// Confirm — the one "are you sure?" the workspace asks before something is deleted for good.
import React, { useEffect, useRef, useState } from 'react';

export interface ConfirmAsk {
  title: string;
  body: string;
  /** The button's words, e.g. "Delete". */
  action: string;
  run: () => Promise<void>;
  /** A gentler way out beside it — "Remove from project" next to "Delete" (Nov 4 2026). */
  alt?: { action: string; busy: string; run: () => Promise<void> };
}

const Confirm: React.FC<{ ask: ConfirmAsk | null; onClose: () => void }> = ({ ask, onClose }) => {
  const [busy, setBusy] = useState<'' | 'main' | 'alt'>('');
  const [error, setError] = useState<string | null>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setBusy(''); setError(null);
    if (!ask) return;
    cancel.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [ask, onClose]);
  if (!ask) return null;
  const go = async (which: 'main' | 'alt') => {
    setBusy(which); setError(null);
    try { await (which === 'alt' && ask.alt ? ask.alt.run() : ask.run()); onClose(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(''); }
  };
  return (
    <div className="ws-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ws-modal" role="alertdialog" aria-modal="true" aria-labelledby="ws-confirm-title" aria-describedby="ws-confirm-body">
        <h3 id="ws-confirm-title">{ask.title}</h3>
        <p id="ws-confirm-body" className="ws-modal-lede">{ask.body}</p>
        {error && <div className="ws-form-error" role="alert">{error}</div>}
        <div className="ws-modal-acts">
          <button ref={cancel} type="button" className="ws-btn" onClick={onClose}>Cancel</button>
          {ask.alt && <button type="button" className="ws-btn" onClick={() => go('alt')} disabled={!!busy}>{busy === 'alt' ? ask.alt.busy : ask.alt.action}</button>}
          <button type="button" className="ws-btn ws-danger" onClick={() => go('main')} disabled={!!busy}>{busy === 'main' ? 'Deleting…' : ask.action}</button>
        </div>
      </div>
    </div>
  );
};

export default Confirm;
